import React from "react";
import { isKnownNonUsInvoiceCountry } from "@common/helpers/nativeInvoice";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import {
    InvoiceAddressCountryField,
    InvoiceAddressStateField,
} from "./invoiceAddressFields";
import styles from "./SidePanel.module.scss";

export interface InvoicePartyAddressValues {
    country: string;
    street: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
    phoneNumber: string;
}

// Address block shared by the From and To columns of the Details step, so
// both sides expose the same fields in the same order. State is a US-specific
// concept: it is hidden (and cleared on country change) only for a known
// non-US country — empty or unrecognized countries keep it visible, matching
// the render/save semantics of isKnownNonUsInvoiceCountry.
export function InvoicePartyAddressFields({
    values,
    onChange,
}: {
    values: InvoicePartyAddressValues;
    onChange: (patch: Partial<InvoicePartyAddressValues>) => void;
}) {
    const hasStateField = !isKnownNonUsInvoiceCountry(values.country);
    const cityField = (
        <Field label="City">
            <Input
                size="xl"
                maxLength={255}
                value={values.city}
                onChange={(event) => onChange({ city: event.target.value })}
            />
        </Field>
    );
    const zipField = (
        <Field label="ZIP">
            <Input
                size="xl"
                maxLength={50}
                value={values.postalCode}
                onChange={(event) =>
                    onChange({ postalCode: event.target.value })
                }
            />
        </Field>
    );
    const phoneField = (
        <Field label="Phone number">
            <Input
                size="xl"
                maxLength={50}
                value={values.phoneNumber}
                placeholder="(555) 555-5555"
                onChange={(event) =>
                    onChange({ phoneNumber: event.target.value })
                }
            />
        </Field>
    );

    return (
        <>
            <InvoiceAddressCountryField
                value={values.country}
                onChange={(value) =>
                    onChange({
                        country: value,
                        state: isKnownNonUsInvoiceCountry(value)
                            ? ""
                            : values.state,
                    })
                }
            />
            <div className={styles.fieldGrid}>
                <Field label="Street line 1">
                    <Input
                        size="xl"
                        maxLength={255}
                        value={values.street}
                        onChange={(event) =>
                            onChange({ street: event.target.value })
                        }
                    />
                </Field>
                <Field label="Street line 2">
                    <Input
                        size="xl"
                        maxLength={255}
                        value={values.street2}
                        onChange={(event) =>
                            onChange({ street2: event.target.value })
                        }
                    />
                </Field>
            </div>
            {hasStateField ? (
                <>
                    <div className={styles.fieldGrid}>
                        {cityField}
                        <InvoiceAddressStateField
                            value={values.state}
                            onChange={(value) => onChange({ state: value })}
                        />
                    </div>
                    <div className={styles.fieldGrid}>
                        {zipField}
                        {phoneField}
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.fieldGrid}>
                        {cityField}
                        {zipField}
                    </div>
                    <div className={styles.fieldGrid}>{phoneField}</div>
                </>
            )}
        </>
    );
}
